import React, { useId, useState } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  Chip,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Typography,
} from '@mui/material';
import MoreVertOutlinedIcon from '@mui/icons-material/MoreVertOutlined';
import { brand, clickableCardSx } from '../../theme';
import LibraryPoster from './LibraryPoster';
import { joinMeta } from './libraryFormat';

export interface LibraryCardAction {
  label: string;
  icon: React.ReactNode;
  onClick: () => void;
  disabled?: boolean;
  /** Delete and the like: shown in the error colour. */
  destructive?: boolean;
  testId?: string;
}

interface LibraryCardProps {
  title: string;
  /** Facts under the title, joined with " · " ("2:14 · 173.6 MB"). Unknown parts are left out. */
  meta: Array<string | null | undefined | false>;
  /** The video a poster frame is taken from. */
  posterSrc?: string | null;
  /** A status chip beside the title (multicam sets). */
  status?: React.ReactNode;
  /** Uploaded during this visit: a small "New" chip and a stronger border. */
  isNew?: boolean;
  /** The card's main action ("Annotate"). Clicking anywhere on the card does the same. */
  openLabel: string;
  openTo: string;
  openState?: unknown;
  /** The overflow (⋮) menu. */
  actions: LibraryCardAction[];
  /** A second visible action beside the main one ("Quick edit"). */
  secondaryAction?: LibraryCardAction;
  testId?: string;
  openTestId?: string;
  menuTestId?: string;
}

/**
 * One card for everything in the Library: a 16:9 poster frame, the name, a
 * line of facts, the main action as a real link, and an overflow menu.
 *
 * The whole card is clickable for mouse users; keyboard and screen-reader
 * users get the same action from the link, so the card itself is not a
 * focus stop (a focusable card wrapping buttons would nest controls).
 */
const LibraryCard: React.FC<LibraryCardProps> = ({
  title,
  meta,
  posterSrc,
  status,
  isNew = false,
  openLabel,
  openTo,
  openState,
  actions,
  secondaryAction,
  testId,
  openTestId,
  menuTestId,
}) => {
  const navigate = useNavigate();
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const menuId = useId();
  const metaText = joinMeta(meta);

  const handleCardClick = () => {
    // Selecting the title to copy it shouldn't open the item.
    if (typeof window !== 'undefined' && window.getSelection?.()?.toString()) return;
    navigate(openTo, { state: openState });
  };

  return (
    <>
      <Card
        data-testid={testId}
        onClick={handleCardClick}
        sx={{
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          ...clickableCardSx,
          ...(isNew ? { borderColor: brand.borderStrong } : {}),
        }}
      >
        <LibraryPoster src={posterSrc}>
          {isNew && (
            <Chip
              label="New"
              size="small"
              sx={{
                position: 'absolute',
                top: 8,
                left: 8,
                height: 22,
                bgcolor: 'background.paper',
                color: 'text.primary',
                border: `1px solid ${brand.borderStrong}`,
              }}
            />
          )}
        </LibraryPoster>

        <Box sx={{ px: 2, pt: 1.5, pb: 1, flex: 1, minWidth: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
            <Typography
              component="h2"
              variant="subtitle1"
              noWrap
              title={title}
              sx={{ flex: 1, minWidth: 0, fontWeight: 600, color: 'text.primary', lineHeight: 1.45 }}
            >
              {title}
            </Typography>
            {status && <Box sx={{ flexShrink: 0 }}>{status}</Box>}
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25, fontVariantNumeric: 'tabular-nums' }}>
            {metaText || '—'}
          </Typography>
        </Box>

        <Box sx={{ px: 2, pb: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
          <Button
            component={RouterLink}
            to={openTo}
            state={openState}
            variant="outlined"
            size="small"
            aria-label={`${openLabel} ${title}`}
            data-testid={openTestId}
            onClick={(event: React.MouseEvent) => event.stopPropagation()}
          >
            {openLabel}
          </Button>
          {secondaryAction ? (
            <Button
              size="small"
              startIcon={secondaryAction.icon}
              disabled={secondaryAction.disabled}
              data-testid={secondaryAction.testId}
              aria-label={`${secondaryAction.label} ${title}`}
              onClick={(event: React.MouseEvent) => { event.stopPropagation(); secondaryAction.onClick(); }}
              sx={{ mr: 'auto' }}
            >
              {secondaryAction.label}
            </Button>
          ) : null}
          <IconButton
            size="small"
            aria-label={`More actions for ${title}`}
            aria-haspopup="menu"
            aria-controls={menuAnchor ? menuId : undefined}
            aria-expanded={menuAnchor ? 'true' : undefined}
            data-testid={menuTestId}
            onClick={(event) => {
              event.stopPropagation();
              setMenuAnchor(event.currentTarget);
            }}
          >
            <MoreVertOutlinedIcon fontSize="small" />
          </IconButton>
        </Box>
      </Card>

      {/* Outside the Card: menu clicks must not bubble into the card's click. */}
      <Menu
        id={menuId}
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={() => setMenuAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{ sx: { minWidth: 180 } }}
      >
        {actions.map((action) => (
          <MenuItem
            key={action.label}
            disabled={action.disabled}
            data-testid={action.testId}
            onClick={() => {
              setMenuAnchor(null);
              action.onClick();
            }}
            sx={action.destructive ? { color: 'error.main' } : undefined}
          >
            <ListItemIcon sx={{ color: action.destructive ? 'error.main' : brand.slate }}>{action.icon}</ListItemIcon>
            <ListItemText>{action.label}</ListItemText>
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};

export default LibraryCard;
